import { AuthenticatedEvent, ExitAction, SessionExpiredEvent } from './messages'
import type {
  AddToCalendarDelegate,
  AuthTokenDelegate,
  CreateExternalUrlHandleOptions,
  DocumentDownloadDelegate,
  ExternalUrlDelegate,
  ExternalUrlOpenHandle,
  LifecycleDelegate,
  PlatformDelegate,
} from './types'

class DefaultExternalUrlDelegate implements ExternalUrlDelegate {
  createHandle(
    options?: CreateExternalUrlHandleOptions,
  ): ExternalUrlOpenHandle {
    if (options?.sameWindow) {
      return {
        open(url: string) {
          window.location.href = url
        },
        abort() {
          // no-op
        },
      }
    }

    const newWindow = window.open('about:blank', '_blank')
    return {
      open(url: string) {
        if (newWindow) {
          newWindow.location.href = url
        }
      },
      abort() {
        newWindow?.close()
      },
    }
  }
}

class DefaultDocumentDownloadDelegate implements DocumentDownloadDelegate {
  download(url: string, preview: boolean): void {
    if (preview) {
      window.open(url, '_blank')
      return
    }

    const link = document.createElement('a')
    link.href = url
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

class DefaultAddToCalendarDelegate implements AddToCalendarDelegate {
  addToCalendar(icsFile: File): void {
    const url = URL.createObjectURL(icsFile)
    const link = document.createElement('a')
    link.href = url
    link.download = 'calendar.ics'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }
}

class DefaultLifecycleDelegate implements LifecycleDelegate {
  get supportsDelegatedExit() {
    return window.parent !== window.self
  }

  notifyAuthenticated(): void {
    window.parent.postMessage(AuthenticatedEvent(), '*')
  }

  notifySessionExpired(): void {
    window.parent.postMessage(SessionExpiredEvent(), '*')
  }

  requestExit(returnUrl: string): void {
    window.parent.postMessage(ExitAction(), '*')
    setTimeout(() => {
      window.location.href = returnUrl
    }, 10)
  }
}

class DefaultAuthTokenDelegate implements AuthTokenDelegate {
  async getToken(): Promise<string | null> {
    return null
  }
}

export class DefaultPlatformDelegate implements PlatformDelegate {
  private readonly externalUrlDelegate = new DefaultExternalUrlDelegate()
  private readonly documentDownloadDelegate =
    new DefaultDocumentDownloadDelegate()

  private readonly addToCalendarDelegate = new DefaultAddToCalendarDelegate()
  private readonly lifecycleDelegate = new DefaultLifecycleDelegate()
  private readonly authTokenDelegate = new DefaultAuthTokenDelegate()

  getExternalUrlDelegate = () => this.externalUrlDelegate
  getDocumentDownloadDelegate = () => this.documentDownloadDelegate
  getAddToCalendarDelegate = () => this.addToCalendarDelegate
  getLifecycleDelegate = () => this.lifecycleDelegate
  getAuthTokenDelegate = () => this.authTokenDelegate
}
